import { useState, type FormEvent } from "react";
import { useParams } from "react-router-dom";
import { api, t } from "../../lib/api";
import { useAsync } from "../../lib/hooks";
import { useSession } from "../../lib/session";
import { Empty, ErrorNote } from "../../components/ui";
import type { Engagement } from "../Engagement";

interface PlanItem {
  id: string;
  department: string;
  location: string;
  designation: string | null;
  stakeholderType: string | null;
  targetCount: number;
  notes: string | null;
}

export interface Person {
  id: string;
  pseudonym: string;
  displayName: string | null;
  language: string;
  profile: { department?: string; designation?: string; location?: string; reportingLine?: string; teamSize?: number; experienceYears?: number; stakeholderType?: string };
  planItemId: string | null;
}

const TYPES = ["employee", "manager", "farmer", "supplier", "customer", "partner", "other"];
const TYPE_LABEL: Record<string, string> = { employee: "Employee", manager: "Manager", farmer: "Farmer", supplier: "Supplier", customer: "Customer", partner: "Partner", other: "Other" };

export function PlanTab({ engagement }: { engagement: Engagement }) {
  const { tenant } = useParams();
  const plan = useAsync(() => api.get<PlanItem[]>(`${t(tenant!)}/engagements/${engagement.id}/plan`), [tenant, engagement.id]);
  const people = useAsync(() => api.get<Person[]>(`${t(tenant!)}/engagements/${engagement.id}/interviewees`), [tenant, engagement.id]);
  const lead = engagement.role === "admin" || engagement.role === "lead";
  const [row, setRow] = useState({ department: "", location: "", designation: "", stakeholderType: "employee", targetCount: "3" });
  const [error, setError] = useState<Error | null>(null);
  const [addingPerson, setAddingPerson] = useState(false);

  const addRow = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    try {
      await api.post(`${t(tenant!)}/engagements/${engagement.id}/plan`, { ...row, designation: row.designation || undefined, targetCount: Number(row.targetCount) });
      setRow({ ...row, designation: "", targetCount: "3" });
      await plan.reload();
    } catch (err) {
      setError(err as Error);
    }
  };

  return (
    <div className="stack" style={{ gap: 24 }}>
      <section className="panel">
        <div className="panel-head">
          <h3>Interview plan</h3>
          <p>Who to hear from, by department and location. Coverage on the dashboard is measured against this plan.</p>
        </div>
        {plan.data?.length === 0 && <Empty title="No plan yet" />}
        {!!plan.data?.length && (
          <table className="table" style={{ marginBottom: 16 }}>
            <thead>
              <tr>
                <th>Department</th>
                <th>Location</th>
                <th>Role</th>
                <th>Group</th>
                <th>Interviews</th>
                <th />
              </tr>
            </thead>
            <tbody>
              {plan.data.map((p) => (
                <tr key={p.id}>
                  <td>{p.department}</td>
                  <td>{p.location}</td>
                  <td>{p.designation ?? "Any"}</td>
                  <td>{p.stakeholderType ? TYPE_LABEL[p.stakeholderType] : "—"}</td>
                  <td>{p.targetCount}</td>
                  <td>
                    {lead && (
                      <button className="btn ghost small" onClick={async () => {
                        await api.del(`${t(tenant!)}/engagements/${engagement.id}/plan/${p.id}`);
                        await plan.reload();
                      }}>
                        Remove
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
        {lead && (
          <form className="row wrap" onSubmit={addRow} style={{ alignItems: "flex-end" }}>
            <label className="field" style={{ flex: "1 1 160px" }}>
              <span>Department</span>
              <input className="input" value={row.department} onChange={(e) => setRow({ ...row, department: e.target.value })} required />
            </label>
            <label className="field" style={{ flex: "1 1 160px" }}>
              <span>Location</span>
              <input className="input" value={row.location} onChange={(e) => setRow({ ...row, location: e.target.value })} required />
            </label>
            <label className="field" style={{ flex: "1 1 160px" }}>
              <span>Role (optional)</span>
              <input className="input" value={row.designation} onChange={(e) => setRow({ ...row, designation: e.target.value })} />
            </label>
            <label className="field" style={{ flex: "0 1 140px" }}>
              <span>Group</span>
              <select className="input" value={row.stakeholderType} onChange={(e) => setRow({ ...row, stakeholderType: e.target.value })}>
                {TYPES.map((x) => (
                  <option key={x} value={x}>
                    {TYPE_LABEL[x]}
                  </option>
                ))}
              </select>
            </label>
            <label className="field" style={{ flex: "0 1 100px" }}>
              <span>Interviews</span>
              <input className="input" type="number" min={1} value={row.targetCount} onChange={(e) => setRow({ ...row, targetCount: e.target.value })} />
            </label>
            <button className="btn">Add to plan</button>
          </form>
        )}
        <ErrorNote error={error} />
      </section>

      <section className="panel">
        <div className="panel-head">
          <h3>People</h3>
          <p>
            Each person gets a pseudonym. {engagement.anonymiseNames ? "Names are optional, encrypted, and never appear in reports." : "Names are encrypted and never appear in reports."}
          </p>
          <span className="spacer" />
          <button className="btn small" onClick={() => setAddingPerson((v) => !v)}>
            {addingPerson ? "Close" : "Add person"}
          </button>
        </div>
        {addingPerson && (
          <div style={{ marginBottom: 20 }}>
            <PersonForm engagement={engagement} onDone={async () => {
              setAddingPerson(false);
              await people.reload();
            }} />
          </div>
        )}
        {people.data?.length === 0 && <Empty title="No people added yet" />}
        {!!people.data?.length && (
          <table className="table">
            <thead>
              <tr>
                <th>Pseudonym</th>
                <th>Role</th>
                <th>Department</th>
                <th>Location</th>
                <th>Language</th>
              </tr>
            </thead>
            <tbody>
              {people.data.map((p) => (
                <tr key={p.id}>
                  <td>
                    <b>{p.pseudonym}</b>
                    {p.displayName && <div className="muted xs">{p.displayName}</div>}
                  </td>
                  <td>{p.profile.designation ?? "—"}</td>
                  <td>{p.profile.department ?? "—"}</td>
                  <td>{p.profile.location ?? "—"}</td>
                  <td>{p.language}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </section>
    </div>
  );
}

/** Profile captured in under a minute: mostly picked from the plan's own values. */
export function PersonForm({ engagement, onDone }: { engagement: Engagement; onDone: (id?: string) => void }) {
  const { tenant } = useParams();
  const { config } = useSession();
  const plan = useAsync(() => api.get<PlanItem[]>(`${t(tenant!)}/engagements/${engagement.id}/plan`), [tenant, engagement.id]);
  const [f, setF] = useState({
    displayName: "",
    language: engagement.intervieweeLanguages[0]!,
    department: "",
    designation: "",
    location: "",
    reportingLine: "",
    teamSize: "",
    experienceYears: "",
    stakeholderType: "employee",
    planItemId: "",
  });
  const [error, setError] = useState<Error | null>(null);
  const departments = [...new Set((plan.data ?? []).map((p) => p.department))];
  const locations = [...new Set((plan.data ?? []).map((p) => p.location))];

  const submit = async (e: FormEvent) => {
    e.preventDefault();
    setError(null);
    const match = (plan.data ?? []).find((p) => p.department === f.department && p.location === f.location);
    try {
      const r = await api.post<{ id: string }>(`${t(tenant!)}/engagements/${engagement.id}/interviewees`, {
        displayName: f.displayName || undefined,
        language: f.language,
        planItemId: match?.id,
        profile: {
          department: f.department || undefined,
          designation: f.designation || undefined,
          location: f.location || undefined,
          reportingLine: f.reportingLine || undefined,
          teamSize: f.teamSize ? Number(f.teamSize) : undefined,
          experienceYears: f.experienceYears ? Number(f.experienceYears) : undefined,
          stakeholderType: f.stakeholderType,
          custom: {},
        },
      });
      onDone(r.id);
    } catch (err) {
      setError(err as Error);
    }
  };

  return (
    <form className="stack" onSubmit={submit}>
      <div className="grid2">
        <label className="field">
          <span>Department</span>
          <input className="input" list="gk-deps" value={f.department} onChange={(e) => setF({ ...f, department: e.target.value })} required />
          <datalist id="gk-deps">{departments.map((d) => <option key={d} value={d} />)}</datalist>
        </label>
        <label className="field">
          <span>Location</span>
          <input className="input" list="gk-locs" value={f.location} onChange={(e) => setF({ ...f, location: e.target.value })} required />
          <datalist id="gk-locs">{locations.map((d) => <option key={d} value={d} />)}</datalist>
        </label>
        <label className="field">
          <span>Role or designation</span>
          <input className="input" value={f.designation} onChange={(e) => setF({ ...f, designation: e.target.value })} />
        </label>
        <label className="field">
          <span>Group</span>
          <select className="input" value={f.stakeholderType} onChange={(e) => setF({ ...f, stakeholderType: e.target.value })}>
            {TYPES.map((x) => (
              <option key={x} value={x}>
                {TYPE_LABEL[x]}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Reports to</span>
          <input className="input" value={f.reportingLine} onChange={(e) => setF({ ...f, reportingLine: e.target.value })} />
        </label>
        <div className="grid2" style={{ gap: 12 }}>
          <label className="field">
            <span>Team size</span>
            <input className="input" type="number" min={0} value={f.teamSize} onChange={(e) => setF({ ...f, teamSize: e.target.value })} />
          </label>
          <label className="field">
            <span>Years in role</span>
            <input className="input" type="number" min={0} step="0.5" value={f.experienceYears} onChange={(e) => setF({ ...f, experienceYears: e.target.value })} />
          </label>
        </div>
        <label className="field">
          <span>Language</span>
          <select className="input" value={f.language} onChange={(e) => setF({ ...f, language: e.target.value })}>
            {[...new Set([...engagement.intervieweeLanguages, ...engagement.interviewerLanguages])].map((c) => (
              <option key={c} value={c}>
                {config.languages.find((l) => l.code === c)?.name ?? c}
              </option>
            ))}
          </select>
        </label>
        <label className="field">
          <span>Name (optional, encrypted)</span>
          <input className="input" value={f.displayName} onChange={(e) => setF({ ...f, displayName: e.target.value })} autoComplete="off" />
        </label>
      </div>
      <ErrorNote error={error} />
      <div className="row">
        <span className="spacer" />
        <button type="button" className="btn" onClick={() => onDone()}>
          Cancel
        </button>
        <button className="btn primary">Save person</button>
      </div>
    </form>
  );
}
